import React from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogTitleProps,
    Divider,
    IconButton,
    IconButtonProps,
    Link,
    LinkProps,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as Styles from "pages/requests/stepsIndividual/standalone-step5/step5.styles";
import FileUpload, { FileUploadType } from 'components/formsUI/FileUpload';
import * as documentService from "services/document-service";
import { useParams } from 'react-router-dom';
import { useSettingsContext } from 'contexts/settingsContext';
import { useTranslation } from 'react-i18next';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface IProps {
    open: boolean;
    onClose: () => void;
    onSubmit: React.Dispatch<React.SetStateAction<boolean>>;
    showMessage: MessageBox;
    downloadApplicationToSign: () => void;
    downloadVertrouwelijkheidsovereenkomstToSign: () => void;
}

const ManualSignModal: React.FC<IProps> = (props: IProps) => {
    const {
        open,
        onClose,
        onSubmit,
        showMessage,
        downloadApplicationToSign,
        downloadVertrouwelijkheidsovereenkomstToSign
    } = props;
    const { getSetting } = useSettingsContext();
    const { applicationId, completionHash } = useParams();
    const theme = useTheme();
    const { t: _t } = useTranslation();
    const [disableFileUpload, setDisableFileUpload] = React.useState<boolean>(false);
    const [vertrDisableFileUpload, setVertrDisableFileUpload] = React.useState<boolean>(false);
    const [aanvraagFiles, setAanvraagFiles] = React.useState<FileUploadType[]>([]);
    const [vertrouwelijkFiles, setVertrouwelijkFiles] = React.useState<FileUploadType[]>([]);

    const t = (key: string) => {
        var multiple = getSetting<boolean>('useVertrouwelijk');
        return _t(key, { count: multiple ? 2 : 1});
    }

    const closeDialogIconButton: IconButtonProps = {
        sx: Styles.closeDialogIconButton(theme)
    };

    const statusDialogTitle: DialogTitleProps = {
        sx: Styles.statusDialogTitle(theme)
    };

    const cancelButtonProps: ButtonProps = {
        variant: "contained",
        color: 'error',
        onClick: onClose
    }

    const saveButtonProps: ButtonProps = {
        variant: "contained",
        color: 'success',
        onClick: () => {
            onSubmit(true);
            onClose();
        },
        disabled: !(disableFileUpload && (vertrDisableFileUpload || !getSetting<boolean>('useVertrouwelijk')))
    }

    const linkProps: LinkProps = {
        href: '#',
        underline: 'hover',
        color: 'inherit',
        variant: 'h6',
        sx: {
            textDecoration: 'underline'
        }
    }

    const typographyProps: any = {
        variant: 'h6',
        component: 'div',
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle {...statusDialogTitle}>
                {t('requestIndividual.step5.modal.title')}
                <IconButton {...closeDialogIconButton}
                    aria-label="close"
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <p style={{ paddingTop: 0, marginTop: 0 }}>
                    {t('requestIndividual.step5.modal.description')}
                </p>
                {/* Aanvraagformulier */}
                {/* TODO: add onClick listener to download the file */}
                <Link {...linkProps} onClick={downloadApplicationToSign}>
                    {/* <Typography {...typographyProps}>{t('requestIndividual.step5.modal.aanvraagTitle')}</Typography> */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <PictureAsPdfIcon fontSize='medium' sx={{marginRight: '0.5rem'}} />
                        {t('requestIndividual.step5.modal.aanvraagTitle')}
                    </Box>
                </Link>
                <FileUpload
                    showMessage={showMessage}
                    completionHash={completionHash}
                    setFilesUploaded={(_) => { setDisableFileUpload(_) }}
                    getFiles={(_) => { setAanvraagFiles(_) }}
                    fileUploads={aanvraagFiles}
                    title=''
                    textUpload={t('requestIndividual.step5.modal.aanvraagDescription')}
                    isStep5={true}
                    disable={disableFileUpload}
                    disableDelete={true}
                    fileType={2}
                />
                {/* Vertrouwelijkheidsovereenkomst */}
                {getSetting<boolean>('useVertrouwelijk') &&
                    <>
                        <Link {...linkProps} onClick={downloadVertrouwelijkheidsovereenkomstToSign}>
                            {/* <Typography {...typographyProps}>{t('requestIndividual.step5.modal.vertrouwelijkTitle')}</Typography> */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <PictureAsPdfIcon sx={{marginRight: '0.5rem'}} />
                                {t('requestIndividual.step5.modal.vertrouwelijkTitle')}
                            </Box>
                        </Link>
                        <FileUpload
                            showMessage={showMessage}
                            completionHash={completionHash}
                            setFilesUploaded={(_) => { setVertrDisableFileUpload(_) }}
                            getFiles={(_) => { setVertrouwelijkFiles(_) }}
                            fileUploads={vertrouwelijkFiles}
                            title=''
                            textUpload={t('requestIndividual.step5.modal.vertrouwelijkDescription')}
                            isStep5={true}
                            isVertr={true}
                            disable={vertrDisableFileUpload}
                            disableDelete={true}
                            fileType={6}
                        />
                    </>
                }
            </DialogContent>
            <DialogActions>
                <Button {...cancelButtonProps}>{t('buttons.cancel')}</Button>
                <Button {...saveButtonProps}>{t('buttons.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManualSignModal;